/**
 * 多選狀態的計算。全部是純函式且回傳新的 Set，不改動輸入——
 * store 只持有狀態，判斷邏輯集中在這裡以便單元測試。
 */

export function toggleSelection(selection: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(selection)
  if (!next.delete(id)) next.add(id)
  return next
}

/**
 * 全選：把給定的識別碼併入選取，不移除落在給定範圍之外的既有選取。
 * 呼叫端傳入的是目前篩選結果，因此「篩選後全選」不會動到篩選外已選好的作品。
 */
export function selectAll(selection: ReadonlySet<string>, ids: readonly string[]): Set<string> {
  return new Set([...selection, ...ids])
}

export function clearSelection(): Set<string> {
  return new Set()
}

/**
 * 修剪至仍然存在的作品。清單更新後立即呼叫，讓「已選取 N 本」不可能指到
 * 不存在的作品。判準是「仍存在於書庫」而不是「仍符合目前篩選」——
 * 篩選條件改變不應該讓已經選好的作品悄悄掉出選取。
 */
export function pruneSelection(selection: ReadonlySet<string>, existing: ReadonlySet<string>): Set<string> {
  const next = new Set<string>()
  for (const id of selection) if (existing.has(id)) next.add(id)
  return next
}

/** 選取中有多少本落在給定範圍（通常是目前的篩選結果）內。 */
export function countWithin(selection: ReadonlySet<string>, ids: readonly string[]): number {
  let count = 0
  for (const id of ids) if (selection.has(id)) count += 1
  return count
}

/**
 * 範圍選取：把錨點到目標之間（含兩端）的作品一併選上。
 *
 * 依「目前看到的順序」計算，而不是書庫的原始順序——使用者按住 shift 點的是螢幕上
 * 那一段，篩選或排序改變時那一段的內容也該跟著改變。
 *
 * 一律是加入而非切換：拖出一整段之後再反向拖一次，預期是範圍變大，不是把已選的取消掉。
 * 錨點或目標已不在目前的清單裡（例如被篩掉）時，退回只處理目標那一本。
 */
export function selectRange(
  selection: ReadonlySet<string>, ids: readonly string[], anchorId: string, targetId: string,
): Set<string> {
  const from = ids.indexOf(anchorId)
  const to = ids.indexOf(targetId)
  if (from === -1 || to === -1) return selectAll(selection, [targetId].filter(id => ids.includes(id) || id === targetId))
  const [start, end] = from <= to ? [from, to] : [to, from]
  return selectAll(selection, ids.slice(start, end + 1))
}
